/**
 * Drafting a correction (admin doc 17 §3.3) — the maker's half of maker-checker.
 *
 * **Two steps, on purpose.** Save writes a draft nobody else sees; "Send for approval" puts it in
 * the queue below, where somebody other than its author approves it. Nothing touches the ledger
 * until then, and the copy says so, because "I clicked it, why is the balance unchanged" is the
 * question this would otherwise generate.
 *
 * **It must name what it corrects.** A refund from this page, or a booking by its number — the
 * server refuses one that names neither, and the form refuses it first.
 */
import { useState } from 'react';
import { Note } from '../components/primitives';
import { Input, MoneyField, Select } from '../components/forms';
import { ScopeAction, Section } from '../components/kit';
import { ErrorState } from '../components/feedback';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import { formatInr, parsePaise } from '../lib/money';
import {
  useCreateAdjustment,
  useSubmitAdjustment,
  type AdjustmentRow,
  type AdminRefundRow,
} from '../lib/api/hooks/refunds';

/** The server's closed set (`AdjustmentReasonCode`). */
const REASONS = [
  'goodwill_credit',
  'pricing_error',
  'recon_difference',
  'provider_penalty',
  'tds_correction',
  'gateway_fee_correction',
  'write_off',
  'other',
] as const;

const NO_REFUND = 'No refund — name a booking';

export function AdjustmentDraft({ refunds }: { refunds: readonly AdminRefundRow[] }) {
  const { scope, toast } = useConsole();
  const create = useCreateAdjustment();
  const submit = useSubmitAdjustment();
  const [direction, setDirection] = useState('credit');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState<string>('goodwill_credit');
  const [memo, setMemo] = useState('');
  const [refundLabel, setRefundLabel] = useState(NO_REFUND);
  const [bookingId, setBookingId] = useState('');
  const [draft, setDraft] = useState<AdjustmentRow | null>(null);

  const paise = parsePaise(amount);
  // Code and amount: two refunds on one booking differ by the amount long before the code is read.
  const labelOf = (r: AdminRefundRow) => `${r.public_code} · ${formatInr(r.amount_paise, { paise: true })}`;
  const refund = refunds.find((r) => labelOf(r) === refundLabel);
  const bookingError = bookingId === '' || /^[1-9]\d*$/.test(bookingId) ? undefined : 'A booking number';
  const named = refund !== undefined || (bookingId !== '' && !bookingError);
  const valid = paise !== null && paise > 0 && named && memo.trim().length >= 3;

  const reset = () => {
    setAmount('');
    setMemo('');
    setRefundLabel(NO_REFUND);
    setBookingId('');
    setDraft(null);
  };

  return (
    <Section title="Draft a correction" note="finance only · posts nothing until somebody else approves it">
      <Note>
        Saved as a draft first. Send it for approval when it reads right — it then waits in the queue below for a second
        pair of eyes, and only an approval touches the ledger.
      </Note>
      {draft === null ? (
        <div style={{ display: 'flex', gap: 'var(--sp-md)', alignItems: 'end', flexWrap: 'wrap', marginTop: 'var(--sp-md)' }}>
          <Select label="Direction" value={direction} options={['credit', 'debit']} onChange={setDirection} />
          <MoneyField label="Amount (₹)" value={amount} onChange={setAmount} positive style={{ width: 140 }} />
          <Select label="Correction reason" value={reason} options={REASONS} onChange={setReason} />
          <Select
            label="Refund"
            value={refundLabel}
            options={[NO_REFUND, ...refunds.map(labelOf)]}
            onChange={setRefundLabel}
          />
          {refund === undefined && (
            <Input label="Booking number" inputMode="numeric" value={bookingId} onChange={setBookingId} error={bookingError} style={{ width: 140 }} />
          )}
          <Input label="Memo" value={memo} onChange={setMemo} helper="Why — kept with the correction" />
          <ScopeAction
            scope={scope}
            need="finance"
            disabled={!valid || create.isPending}
            onClick={() =>
              create.mutate(
                {
                  direction,
                  amount_paise: paise!,
                  reason_code: reason,
                  memo: memo.trim(),
                  booking_id: refund ? refund.booking_id : Number(bookingId),
                  refund_id: refund ? refund.id : null,
                },
                { onSuccess: setDraft },
              )
            }
          >
            Save draft
          </ScopeAction>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 'var(--sp-md)', alignItems: 'center', flexWrap: 'wrap', marginTop: 'var(--sp-md)' }}>
          <span style={{ fontSize: 'var(--fs-admin-ui)' }}>
            Draft #{draft.id} · {draft.direction} {formatInr(draft.amount_paise, { paise: true })} · {draft.reason_code}
          </span>
          <ScopeAction
            scope={scope}
            need="finance"
            disabled={submit.isPending}
            onClick={() =>
              submit.mutate(draft.id, {
                onSuccess: () => {
                  toast({ tone: 'success', message: `Draft #${draft.id} sent for approval` });
                  reset();
                },
              })
            }
          >
            Send for approval
          </ScopeAction>
          <ScopeAction scope={scope} need="finance" variant="ghost" onClick={reset}>
            Start another
          </ScopeAction>
        </div>
      )}
      {create.isError && <ErrorState inline title="The server refused this draft" cause={errorMessage(create.error)} />}
      {submit.isError && <ErrorState inline title="It could not be sent for approval" cause={errorMessage(submit.error)} />}
    </Section>
  );
}
